import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { ChevronRight } from 'lucide-react';

import { useAccordionItem } from './useAccordionItem';

const ItemContainer = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme?.colors?.border?.subtle};
`;

const HeaderButton = styled.button`
  width: 100%;
  background: transparent;
  border: 0;
  margin: 0;
  padding: 14px 0;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${({ theme }) => theme?.colors?.neutral?.[500]};
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  gap: 4px;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme?.colors?.primary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const HeaderLeft = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  line-height: 20px;
  flex-grow: 1;
  color: ${({ theme }) => theme?.colors?.text?.dark};
`;

const ChevronIcon = styled(ChevronRight)`
  width: 16px;
  height: 16px;
  transition: transform 200ms ease;
  ${({ $open }) =>
    $open &&
    css`
      transform: rotate(90deg);
    `}
`;

const AnimatedSection = styled.div`
  overflow: hidden;
  transition: height 220ms ease, opacity 220ms ease;
  height: ${({ $open, $contentHeight }) => ($open ? `${$contentHeight}px` : '0px')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
`;

const Badge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme?.colors?.purple?.[100]};
  color: ${({ theme }) => theme?.colors?.purple?.[400]};
  height: 16px;
  min-width: 16px;
  padding: 0px 4px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
`;

export const AccordionItem = ({ id, title, badge, children, isOpen, onToggle }) => {
  const { measuredHeight, handleRef } = useAccordionItem();
  const buttonId = `${id}-header`;
  const regionId = `${id}-region`;

  return (
    <ItemContainer>
      <HeaderButton
        id={buttonId}
        type="button"
        aria-controls={regionId}
        aria-expanded={isOpen}
        onClick={() => onToggle(id)}
      >
        <HeaderLeft>{title}</HeaderLeft>
        {badge && <Badge>{badge}</Badge>}
        <ChevronIcon $open={isOpen} />
      </HeaderButton>
      <AnimatedSection
        role="region"
        id={regionId}
        aria-labelledby={buttonId}
        $open={isOpen}
        $contentHeight={measuredHeight}
      >
        <div style={{ padding: '4px 0 12px 0' }} ref={handleRef}>
          {children}
        </div>
      </AnimatedSection>
    </ItemContainer>
  );
};

AccordionItem.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
  badge: PropTypes.string,
  children: PropTypes.node,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

AccordionItem.defaultProps = {
  children: null,
  badge: '',
};
